import React, { useState, useEffect, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import axios from "axios";
import Select from "react-select";
import { useAuthToken } from "../TokenContext";
import { useIntl } from "react-intl";
import PersonIcon from "@mui/icons-material/Person";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import moment from "moment";
import {
  Drawer,
  TextField,
  Button,
  CircularProgress,
  alpha,
  useTheme,
  styled,
  Paper,
  Typography,
  Box,
  Checkbox,
  Fab,
  Slide,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress
} from "@mui/material";
import CodeIcon from "@mui/icons-material/Code";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useSelector } from "react-redux";
import "./kanband.css";
import { Snackbar, Alert } from '@mui/material';

import { FaBug, FaJira } from 'react-icons/fa';
import agenticApi from "../utils/agenticApi";

// Styled Components
const StyledBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})(({ theme, collapsed }) => ({
  position: 'relative',
  width: '100%',
  marginLeft: 0,
  overflowX: 'hidden',
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  [theme.breakpoints.down('md')]: {
    marginLeft: 0,
    width: '100%'
  },
  backgroundColor: theme.palette.mode === 'dark' ? 'transparent' : '#F5F5F5',
  color: theme.palette.text.primary,
  minHeight: 'calc(100vh - 64px)',
  padding: theme.spacing(3)
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#fff',
    color: theme.palette.text.primary,
  }
}));

const FiltersContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    '& > *': {
      width: '100%'
    }
  }
}));

const SelectWrapper = styled(Box)(({ theme }) => ({
  width: "20rem",
  marginBottom: theme.spacing(4),
  '& .select__control': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
    borderColor: theme.palette.divider,
    minHeight: 56,
    '&:hover': {
      borderColor: theme.palette.primary.main,
    }
  },
  '& .select__menu': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
    color: theme.palette.text.primary,
    zIndex: 999,
    boxShadow: theme.shadows[3]
  },
  '& .select__option': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
    color: theme.palette.text.primary,
    padding: theme.spacing(1),
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.1)
    }
  }
}));

const SprintSelect = styled('select')(({ theme }) => ({
  textDecoration: 'none',
  border: '1px solid rgb(233, 224, 224)',
  borderRadius: '4px',
  width: "20rem",
  height: '56px',
  padding: '0 0.5rem',
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
  color: theme.palette.text.primary,
  cursor: 'pointer',
  '&:focus': {
    borderColor: theme.palette.primary.main,
    outline: 'none',
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
  }
}));

const StyledColumn = styled(Box)(({ theme, collapsed }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  minWidth: '280px',
  width: '280px',
  marginRight: theme.spacing(2),
  maxHeight: 'calc(100vh - 220px)',
  overflowY: 'auto',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    display: 'none'
  },
  boxShadow: theme.shadows[1],
  '& .column-header': {
    borderBottom: '3px solid',
    paddingBottom: theme.spacing(1),
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  }
}));

const StyledTask = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#fff',
  border: `1px solid ${theme.palette.divider}`,
  borderLeft: `6px solid ${theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[2]
  },
  '& .task-content': {
    width: '100%',
    textAlign: 'left'
  },
  '& .task-header': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  '& .task-title': {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.primary
  },
  '& .task-id': {
    fontSize: '0.75rem',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: '2px 8px',
    borderRadius: '4px',
    display: 'inline-block',
    marginBottom: theme.spacing(1)
  },
  '& .task-assignee': {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '0.75rem',
    color: theme.palette.text.secondary
  },
  '& .task-due-date': {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5)
  }
}));

const ScrollButton = styled(Button)(({ theme }) => ({
  position: 'fixed',
  top: '50%',
  transform: 'translateY(-50%)',
  width: 40,
  height: 40,
  minWidth: 40,
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '50%',
  boxShadow: theme.shadows[2],
  zIndex: 1,
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  }
}));

const ActionBar = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
  borderTop: `2px solid ${theme.palette.primary.main}`,
  boxShadow: theme.shadows[8],
  zIndex: 1100,
}));

const KanbanBoard = ({ collapsed }) => {
  // State declarations
  const [tasks, setTasks] = useState([]);
  const [columns, setColumns] = useState({
    "To Do": { title: "To Do", items: [], type: "open" },
    "In Progress": { title: "In Progress", items: [], type: "open" },
    Done: { title: "Done", items: [], type: "closed" },
  });
  const [selectedPodId, setSelectedPodId] = useState([]);
  const [authorizationDetails, setAuthorizationDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState([]);
  const [podNames, setPodNames] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [agents, setAgents] = useState([]);

  const theme = useTheme();
  const [sprintData, setSprintData] = useState([]);
  const [selectedSprint, setSelectedSprint] = useState({
    name: "",
    startDate: "",
    endDate: "",
  });
  const token = useAuthToken();
  const intl = useIntl();
  const { user } = useSelector((state) => state.auth);
  const scrollContainerRef = useRef(null);

  // Flow management states
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [selectedAgentForBulk, setSelectedAgentForBulk] = useState(null);
  const [showAgentSelectionDialog, setShowAgentSelectionDialog] = useState(false);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');

  // Launch Agent Pipeline states
  const [isLaunchingPipeline, setIsLaunchingPipeline] = useState(false);
  const [showPipelineResult, setShowPipelineResult] = useState(false);
  const [pipelineResult, setPipelineResult] = useState(null);
  const [deployedUrl, setDeployedUrl] = useState(null);
  const [checkingDeploy, setCheckingDeploy] = useState(false);
  const [deployStatus, setDeployStatus] = useState('');

  // ============================================================================
  // COMPLETE FLOW: AGENT ASSIGNMENT → CODE GENERATION → NOTIFICATION
  // ============================================================================

  const handleCompleteFlow = async () => {
    if (selectedTasks.length === 0) {
      alert('Please select at least one task');
      return;
    }

    // If no agents are available, skip selection and fire pipeline directly
    if (agents.length === 0) {
      await handleStartWorkflow(true);
      return;
    }

    // Step 1: Show agent selection dialog
    setShowAgentSelectionDialog(true);
  };

  const handleStartWorkflow = async (skipAgentCheck = false, overrideTasks = null) => {
    if (!skipAgentCheck && !selectedAgentForBulk) {
      alert('Please select an agent');
      return;
    }

    setShowAgentSelectionDialog(false);
    setIsLaunchingPipeline(true);

    const taskIdsToUse = overrideTasks || selectedTasks;
    const selectedTaskDetails = tasks.filter(task =>
      taskIdsToUse.includes(task.key || task.pod_task_id)
    );

    const agent = selectedAgentForBulk
      ? agents.find(a => a.id === selectedAgentForBulk) || null
      : null;

    try {
      // Assign agent to selected tasks locally (only if an agent was chosen)
      if (agent) {
        for (let i = 0; i < selectedTaskDetails.length; i++) {
          const task = selectedTaskDetails[i];
          await assignAgentToTask(task, selectedAgentForBulk, agent);
        }
      }
      setIsLaunchingPipeline(true);

      const userData = user?.data?.[0] || {};
      const firstTask = selectedTaskDetails[0];
      const taskLabel = firstTask?.key || firstTask?.pod_task_id || "App";
      const cleanTitle = `App-${taskLabel}`;

      // Step 1: Create parent workflow
      const step1 = await agenticApi.prdFeatureRequests({
        feature_text: cleanTitle,
        prd_title: cleanTitle,
        manager_id: userData.emp_id || 1618,
        org_code: userData.org_code || userData.talent_code || "CLIENT#DEMO",
        pod_id: selectedPodId && selectedPodId.length > 0 ? selectedPodId[0] : 47,
        approver_id: userData.emp_id || 1618,
        approver_name: userData.firstName || userData.name || "User",
        auto_approve: false,
        deploy_app: false,
      });

      const parentWorkflowId = step1.workflow_id;

      // Step 2: Submit with specific feature requests
      const step2 = await agenticApi.submitSelectedFeatures(parentWorkflowId, {
        submit_all: true,
        deploy_app: true,
        auto_approve: false,
        manager_id: userData.emp_id || 1618,
        org_code: userData.org_code || userData.talent_code || "CLIENT#DEMO",
        pod_id: selectedPodId && selectedPodId.length > 0 ? selectedPodId[0] : 47,
        approver_id: userData.emp_id || 1618,
        approver_name: userData.firstName || userData.name || "User",
      });

      setPipelineResult({ success: true, data: step2, count: selectedTaskDetails.length });
      setSelectedTasks([]);
      setSelectedAgentForBulk(null);

      // Seed immediate URL if already in response
      const immediateUrl = step2.deployment_url || step2.live_app_url || step2.website_url;
      if (immediateUrl) setDeployedUrl(immediateUrl);

    } catch (err) {
      console.error('Launch Agent Pipeline failed:', err);
      setPipelineResult({ success: false, error: err?.response?.data?.message || err.message, count: selectedTaskDetails.length });
    } finally {
      setIsLaunchingPipeline(false);
      setShowPipelineResult(true);
    }
  };

  // Poll for deployment URL in Kanban board dialog — only resolve when health.status === 'pass'
  useEffect(() => {
    if (!showPipelineResult || !pipelineResult?.success || !pipelineResult.data?.workflow_id) return;
    if (deployedUrl) return;

    let attempts = 0;
    let consecutivePasses = 0;
    const MAX_ATTEMPTS = 42; 
    setCheckingDeploy(true);
    const timer = setInterval(async () => {
      attempts++;
      console.log(`[KanbanBoard] Polling deployment health (attempt ${attempts})...`);
      if (!pipelineResult?.data?.workflow_id) {
        console.warn("[KanbanBoard] Workflow ID missing, stopping polling.");
        clearInterval(timer);
        return;
      }
      try {
        const h = await agenticApi.getWorkflowHealth(pipelineResult.data.workflow_id);
        const url = h?.deployment_url || h?.live_app_url || h?.website_url || h?.latest_release?.deployment_url;
        const st = h?.deployment_status || h?.current_status || '';
        setDeployStatus(st);
        
        console.log(`[KanbanBoard] Health status: ${h?.health?.status}, Deployment status: ${st}, URL: ${url}`);
        const isLive = (h?.health?.status === 'pass' || h?.health?.status === 'ok')
          && (st === 'completed' || st === 'deployed' || st === 'success');
        
        if (url && isLive) {
          consecutivePasses++;
          console.log(`[KanbanBoard] Consecutive passes: ${consecutivePasses}/3`);
          if (consecutivePasses >= 3) {
            setDeployedUrl(url);
            setCheckingDeploy(false);
            clearInterval(timer);
          }
        } else {
          consecutivePasses = 0;
        }
      } catch (e) { consecutivePasses = 0; }
      if (attempts >= MAX_ATTEMPTS) {
        setCheckingDeploy(false);
        clearInterval(timer);
      }
    }, 10000);
    return () => clearInterval(timer);
  }, [showPipelineResult, pipelineResult, deployedUrl]);

  const closePipelineResult = () => {
    setShowPipelineResult(false);
    setDeployedUrl(null);
    setCheckingDeploy(false);
    setDeployStatus('');
  };


  const assignAgentToTask = async (task, agentId, agent) => {
    const headers = {
      Authorization: token,
      "Content-Type": "application/json",
    };

    try {
      // Update local state immediately
      setTasks(prevTasks =>
        prevTasks.map(t =>
          (t.key || t.pod_task_id) === (task.key || task.pod_task_id)
            ? { ...t, assigned_agent: agent }
            : t
        )
      );

      // Update columns
      setColumns(prevColumns => {
        const newColumns = { ...prevColumns };
        Object.keys(newColumns).forEach(columnKey => {
          newColumns[columnKey].items = newColumns[columnKey].items.map(t =>
            (t.key || t.pod_task_id) === (task.key || task.pod_task_id)
              ? { ...t, assigned_agent: agent }
              : t
          );
        });
        return newColumns;
      });

      // Make API call (if you have the endpoint)
      if (process.env.REACT_APP_CLIENT_ASSIGNAGENTTOTASK) {
        await axios.post(
          process.env.REACT_APP_CLIENT_ASSIGNAGENTTOTASK,
          {
            task_id: task.key || task.pod_task_id,
            agent_id: agentId,
            task_type: task.key ? 'jira' : 'pod'
          },
          { headers }
        );
      }
    } catch (error) {
      console.error("Error assigning agent:", error);
    }
  };

  const handleCloseNotification = (event, reason) => {
    if (reason === 'clickaway') return;
    setShowNotification(false);
  };

  const handleCloseAgentDialog = () => {
    setShowAgentSelectionDialog(false);
    setSelectedAgentForBulk(null);
  };

  // Initial data fetching
  useEffect(() => {
    if (token && user?.data?.[0]) {
      fetchPodNames();
      fetchAgents();
    }
  }, [token, user]);

  useEffect(() => {
    if (selectedPodId.length > 0) {
      fetchAuthorizationTokensAndList();
    } else {
      resetStates();
    }
  }, [selectedPodId, selectedCandidate, selectedSprint.name]);

  const resetStates = () => {
    setCandidates([]);
    setTasks([]);
    setSelectedTasks([]);
    setColumns({
      "To Do": { title: "To Do", items: [], type: "open" },
      "In Progress": { title: "In Progress", items: [], type: "open" },
      Done: { title: "Done", items: [], type: "closed" },
    });
  };

  // Helper functions
  const parseStatusString = (statusString) => {
    if (!statusString) return [];
    return statusString.split(",").map((status) => status.trim());
  };

  const handleScroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      const currentScroll = scrollContainerRef.current.scrollLeft;
      scrollContainerRef.current.scrollTo({
        left: currentScroll + (direction === "left" ? -scrollAmount : scrollAmount),
        behavior: "smooth",
      });
    }
  };

  // Task selection handlers
  const handleTaskSelection = (taskId, event) => {
    event.stopPropagation();
    setSelectedTasks(prev => {
      if (prev.includes(taskId)) {
        return prev.filter(id => id !== taskId);
      } else {
        return [...prev, taskId];
      }
    });
  };

  const handleClearSelection = () => {
    setSelectedTasks([]);
  };

  // Agent fetching function
  const fetchAgents = async () => {
    console.log("fetchAgents triggered. User data:", user?.data?.[0]);

    const orgId = user?.data?.[0]?.client_code || user?.data?.[0]?.org_code || user?.data?.[0]?.talent_code || user?.data?.[0]?.orgId;

    if (!orgId || !token) {
      console.warn("fetchAgents skipped: Missing orgId or token", {
        orgId,
        hasToken: !!token,
        clientCode: user?.data?.[0]?.client_code,
        orgCode: user?.data?.[0]?.org_code,
        talentCode: user?.data?.[0]?.talent_code
      });
      return;
    }

    const apiUrl = process.env.REACT_APP_CLIENT_LISTAGENTHANDLER;
    console.log("fetchAgents making call to:", apiUrl, "with orgId:", orgId);

    if (!apiUrl) {
      console.error("fetchAgents error: REACT_APP_CLIENT_LISTAGENTHANDLER is not defined in environment variables");
      return;
    }

    const headers = {
      Authorization: token,
      "Content-Type": "application/json",
    };

    try {
      const response = await axios.post(
        apiUrl,
        {
          env: process.env.REACT_APP_ENV || "dev",
          org_id: orgId,
          limit: 100,
          offset: 0
        },
        { headers }
      );

      console.log("fetchAgents response status:", response.status);

      if (response.data.status === 200 && response.data.data.agents) {
        setAgents(response.data.data.agents);
      } else {
        console.warn("fetchAgents: Unexpected response format", response.data);
      }
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
  };



  // Column initialization
  const initializeColumns = (authData) => {
    if (!authData) return columns;

    const openStatuses = parseStatusString(authData.jira_open_story_bug_status?.[0]);
    const closedStatuses = parseStatusString(authData.jira_closed_story_bug_status?.[0]);

    const newColumns = {};
    openStatuses.forEach((status) => {
      newColumns[status] = { title: status, items: [], type: "open" };
    });
    closedStatuses.forEach((status) => {
      newColumns[status] = { title: status, items: [], type: "closed" };
    });

    return newColumns;
  };

  // Task grouping
  const groupTasksByStatus = (tasks, columns) => {
    const groupedColumns = { ...columns };
    Object.keys(groupedColumns).forEach((key) => {
      groupedColumns[key].items = [];
    });

    tasks.forEach((task) => {
      const status = task.status || "To Do";
      if (groupedColumns[status]) {
        groupedColumns[status].items.push(task);
      }
    });

    return groupedColumns;
  };

  // Comment rendering and handling functions
  const renderJiraComment = (content) => {
    if (!content) return null;

    return content.map((item, index) => {
      if (item.type === 'paragraph') {
        return (
          <div key={index} style={{ marginBottom: '8px' }}>
            {item.content?.map((contentItem, cIndex) => {
              if (contentItem.type === 'text') {
                return <span key={cIndex}>{contentItem.text}</span>;
              }
              if (contentItem.type === 'mention') {
                return (
                  <span
                    key={cIndex}
                    style={{
                      color: '#0052CC',
                      fontWeight: '500',
                      backgroundColor: '#EAE6FF',
                      padding: '0 4px',
                      borderRadius: '3px',
                      margin: '0 2px'
                    }}
                  >
                    {contentItem.attrs.text}
                  </span>
                );
              }
              return contentItem.type === 'hardBreak' ? <br key={cIndex} /> : null;
            })}
          </div>
        );
      }
      return null;
    });
  };

  const renderComments = () => {
    if (!selectedTask) return null;

    if (selectedTask.pod_task_id && selectedTask.comment) {
      try {
        const podComments = JSON.parse(selectedTask.comment);
        return podComments.map((comment, index) => (
          <div
            key={index}
            className="comment"
            style={{
              marginBottom: "20px",
              padding: "12px",
              backgroundColor: theme.palette.mode === 'dark'
                ? theme.palette.background.paper
                : '#f8f9fa',
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "12px",
              alignItems: "center",
            }}>
              <span style={{
                backgroundColor: "#0073e6",
                color: "white",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "14px",
                fontWeight: "500",
              }}>
                {comment.author}
              </span>
              <small style={{ color: "#666" }}>
                {moment(comment.timestamp).format("MMM DD, YYYY HH:mm")}
              </small>
            </div>
            <div style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              lineHeight: "1.5",
            }}>
              {comment.text}
            </div>
          </div>
        ));
      } catch (error) {
        console.error("Error parsing POD comments:", error);
        return null;
      }
    }

    return (
      <>
        <Box><Typography variant="subtitle1">Comments</Typography></Box>
        {selectedTask.comments?.map((comment, index) => (
          <div
            key={index}
            className="comment"
            style={{
              marginBottom: "20px",
              padding: "12px",
              backgroundColor: theme.palette.mode === 'dark'
                ? theme.palette.background.paper
                : '#f8f9fa',
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "12px",
              alignItems: "center",
            }}>
              <span style={{
                backgroundColor: "#0073e6",
                color: "white",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "14px",
                fontWeight: "500",
              }}>
                {comment.author}
              </span>
              <small style={{ color: "#666" }}>
                {moment(comment.created).format("MMM DD, YYYY HH:mm")}
              </small>
            </div>
            <div style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              lineHeight: "1.5",
            }}>
              {renderJiraComment(comment.body?.content)}
            </div>
          </div>
        ))}
      </>
    );
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTask) return;

    const headers = {
      Authorization: token,
      "Content-Type": "application/json",
    };

    try {
      if (selectedTask.pod_task_id) {
        let existingComments = [];
        if (selectedTask.comment) {
          try {
            existingComments = JSON.parse(selectedTask.comment);
          } catch (error) {
            console.error("Error parsing existing comments:", error);
            existingComments = [];
          }
        }

        const newCommentObj = {
          pod_task_id: parseInt(selectedTask.pod_task_id),
          user_id: parseInt(user.data[0].emp_id),
          new_text: newComment,
          is_reply: false,
          author: user.data[0].emp_firstname
        };

        const response = await axios.post(
          process.env.REACT_APP_CLIENT_ADDCOMENTPODTASK,
          [newCommentObj],
          { headers }
        );

        if (response.status === 200) {
          setSelectedTask(prevTask => ({
            ...prevTask,
            comment: JSON.stringify([
              ...existingComments,
              {
                text: newComment,
                user_id: parseInt(user.data[0].emp_id),
                timestamp: new Date().toISOString(),
                replies: []
              }
            ])
          }));
          setNewComment("");
        }
      } else if (selectedTask.key) {
        const response = await axios.post(
          process.env.REACT_APP_CLIENT_ADDCOMMENTJIRAPODBOARD,
          {
            jiraUrl: authorizationDetails.Jira_URL,
            issueKey: selectedTask.key,
            commentText: newComment,
            username: authorizationDetails.Jira_admin_login_email_address,
            password: authorizationDetails.Jira_admin_API_Token,
          },
          { headers }
        );

        if (response.status === 200) {
          setSelectedTask((prevTask) => ({
            ...prevTask,
            comments: [
              ...(prevTask.comments || []),
              {
                author: user.data[0].emp_name || "current_user",
                body: {
                  type: "doc",
                  version: 1,
                  content: [
                    {
                      type: "paragraph",
                      content: [{ type: "text", text: newComment }],
                    },
                  ],
                },
                created: new Date().toISOString(),
              },
            ],
          }));
          setNewComment("");
        }
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      alert("Failed to add comment. Please try again.");
    }
  };

  // Data fetching functions
  const fetchPodNames = async () => {
    const headers = {
      Authorization: token,
      "Content-Type": "application/json",
    };
    try {
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_FETCHPODSFORMANAGER,
        { clientID: user.data[0].emp_id },
        { headers }
      );

      const data = response.data.data;
      const podNameOptions = data.map((pod) => ({
        label: pod.podName,
        value: pod.id,
      }));

      setPodNames(podNameOptions);
    } catch (error) {
      console.error("Error fetching pod names:", error);
    }
  };

  useEffect(() => {
    if (user.data[0].type === "stakeholder") {
      if (!token) {
        console.log("Token is missing. API requests will not be made.");
        return;
      }
      fetch(process.env.REACT_APP_STAKEHOLDER_SPRINTFILTERSTAKEHOLDER, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          manager_id: user.data[0].emp_id,
          org_code: user.data[0].client_code,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.statusCode === 200 && data.body) {
            const parsedBody = JSON.parse(data.body);
            if (Array.isArray(parsedBody.sprint_filter)) {
              setSprintData(parsedBody.sprint_filter);
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching data from stakeholder API:", error);
        });
    } else {
      if (!token) {
        console.log("Token is missing. API requests will not be made.");
        return;
      }
      fetch(process.env.REACT_APP_CLIENT_SPRINTFILTERMANAGER, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          manager_id: user.data[0].emp_id,
          org_code: user.data[0].client_code,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.statusCode === 200 && data.body) {
            const parsedBody = JSON.parse(data.body);
            if (Array.isArray(parsedBody.sprint_filter)) {
              setSprintData(parsedBody.sprint_filter);
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
        });
    }
  }, [token, user]);



  const fetchAuthorizationTokensAndList = async () => {
    setLoading(true);

    if (!selectedPodId || selectedPodId.length === 0) {
      setLoading(false);
      return;
    }

    const headers = {
      Authorization: token,
      "Content-Type": "application/json",
    };

    try {
      const authorizationResponse = await axios.post(
        process.env.REACT_APP_CUSTOMERADMIN_FETCHAUTHORIZATIONTOKENS,
        { Organizational_Code: user.data[0].client_code },
        { headers }
      );

      const authorizationData = authorizationResponse.data.body.data[0];
      setAuthorizationDetails(authorizationData);

      const initialColumns = initializeColumns(authorizationData);
      setColumns(initialColumns);

      if (selectedPodId.length > 0) {
        const emailFetchingResponse = await axios.post(
          process.env.REACT_APP_CLIENT_FETCHPODCANDIDATES,
          { podID: selectedPodId },
          { headers }
        );

        const allCandidatesData = emailFetchingResponse?.data?.data;
        const allCandidates = Array.isArray(allCandidatesData) ? allCandidatesData : [];
        setCandidates(allCandidates);

        const candidateNames = selectedCandidate.map(option => option.value);

        if (selectedSprint.name) {
          const listPayload = {
            pod_Id: selectedPodId,
            username: authorizationData?.Jira_admin_login_email_address || "",
            password: authorizationData?.Jira_admin_API_Token || "",
            projectKey: user?.data[0]?.talent_code || user?.data[0]?.org_code || 'G10',
            jira_url: authorizationData?.Jira_URL || "",
            assignees: selectedCandidate.map(option => option.value),
            candidateName: selectedCandidate.map(option => option.value),
            sprintName: selectedSprint.name
          };

          const response = await axios.post(
            process.env.REACT_APP_CLIENT_TESTSINGLEVIEWTASK,
            listPayload,
            { headers }
          );

          let responseData = response.data;

          if (responseData && responseData.body && typeof responseData.body === 'string') {
            try {
              responseData = JSON.parse(responseData.body);
            } catch (e) {
              console.error("Failed to parse response body", e);
            }
          }

          const combinedTasks = [
            ...(responseData?.data?.podMembers || []).map((task) => ({
              ...task,
              comments: task.comments || [],
            })),
            ...(responseData?.data?.jiraIssues || []).map((task) => ({
              ...task,
              comments: task.comments || [],
            })),
          ];

          setTasks(combinedTasks);
          setColumns(groupTasksByStatus(combinedTasks, initialColumns));
        } else {
          setTasks([]);
        }
      }
    } catch (error) {
      console.error("Error in fetchAuthorizationTokensAndList:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskClick = async (task) => {
    const fullTask = tasks.find(
      (t) => (t.key || t.pod_task_id) === (task.key || task.pod_task_id)
    );

    if (fullTask) {
      setSelectedTask(fullTask);
      if (fullTask.pod_task_id) {
        try {
          const headers = {
            Authorization: token,
            "Content-Type": "application/json",
          };

          const response = await axios.post(
            process.env.REACT_APP_CLIENT_FETCHPODTASKCOMMENT,
            { pod_task_id: parseInt(fullTask.pod_task_id) },
            { headers }
          );
        } catch (error) {
          console.error("Error fetching pod task comments:", error);
        }
      }
    } else {
      setSelectedTask({
        ...task,
        comments: [],
      });
    }
  };

  const handleDrawerClose = () => {
    setSelectedTask(null);
    setNewComment("");
    setSelectedAgent(null);
  };

  // Drag and drop handlers
  const onDragEnd = async (result) => {
    if (!result.destination || isUpdating) return;

    const { source, destination, draggableId } = result;

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const task = Object.values(columns).reduce((found, column) => {
      if (found) return found;
      return column.items.find(
        (task) => (task.key || task.pod_task_id).toString() === draggableId
      );
    }, null);

    if (!task) return;

    const oldColumns = columns;
    const newColumns = { ...columns };
    const sourceColumn = columns[source.droppableId];
    const destColumn = columns[destination.droppableId];

    const sourceItems = [...sourceColumn.items];
    const destItems = [...destColumn.items];

    const [removed] = sourceItems.splice(source.index, 1);
    const movedTask = { ...removed, status: destination.droppableId };
    destItems.splice(destination.index, 0, movedTask);

    newColumns[source.droppableId] = {
      ...sourceColumn,
      items: sourceItems,
    };
    newColumns[destination.droppableId] = {
      ...destColumn,
      items: destItems,
    };

    setColumns(newColumns);
    setIsUpdating(true);

    try {
      const headers = {
        Authorization: token,
        "Content-Type": "application/json",
      };

      if (!authorizationDetails?.Jira_admin_API_Token ||
        !authorizationDetails?.Jira_admin_login_email_address ||
        !authorizationDetails?.Jira_URL) {
        throw new Error("Missing authorization details");
      }

      if (task.pod_task_id) {
        const updateResponse = await axios.put(
          process.env.REACT_APP_CLIENT_UPDATEKANBANBOARDPODTASK,
          {
            pod_task_id: task.pod_task_id,
            status: destination.droppableId,
          },
          { headers }
        );

        if (updateResponse.status !== 200) {
          throw new Error("Failed to update pod task status");
        }
      } else {
        const transitionId = await fetchTransitionId(
          destination.droppableId,
          task.key
        );

        if (!transitionId) {
          throw new Error("Invalid transition for this status change");
        }

        const updateResponse = await axios.put(
          process.env.REACT_APP_CLIENT_KANBANBOARDUPDATE,
          {
            Jira_URL: authorizationDetails.Jira_URL,
            Jira_admin_login_email_address: authorizationDetails.Jira_admin_login_email_address,
            Jira_admin_API_Token: authorizationDetails.Jira_admin_API_Token,
            key: task.key,
            transitionId: transitionId,
            status: destination.droppableId,
          },
          {
            headers,
            timeout: 10000
          }
        );

        if (updateResponse.status !== 200) {
          throw new Error("Failed to update JIRA task status");
        }
      }
    } catch (error) {
      console.error("Error updating task status:", error);
      setColumns(oldColumns);
      alert("Failed to update task status. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  const fetchTransitionId = async (status, key) => {
    if (!status || !key) return null;

    const headers = {
      Authorization: token,
      "Content-Type": "application/json",
    };

    try {
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_FETCHSTATUSFORSPECIFICJIRAISSUE,
        {
          Jira_admin_login_email_address: authorizationDetails.Jira_admin_login_email_address,
          Jira_admin_API_Token: authorizationDetails.Jira_admin_API_Token,
          key: key,
          Jira_URL: authorizationDetails.Jira_URL,
          status: status,
        },
        {
          headers,
          timeout: 5000
        }
      );

      if (!response.data?.body) {
        throw new Error("Invalid response format");
      }

      const statusData = response.data.body;
      const matchingStatus = statusData.find((s) => s.name === status);

      return matchingStatus?.id || null;
    } catch (error) {
      console.error("Error fetching transitionId:", error);
      return null;
    }
  };

  // Return/Render section
  // Return/Render section
  return (
    <div>
      <Box sx={{ p: 4 }}>
        <Typography className="artemy-page-title" gutterBottom>
          {intl.formatMessage({ id: "podBoard" })}
        </Typography>
        <Typography className="artemy-page-sub" gutterBottom>
          Manage and track your pod tasks and AI agent work in real-time.
        </Typography>

        <Box className="artemy-card" sx={{ p: 3, mb: 4, bgcolor: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(10px)' }}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ width: '300px' }}>
              <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)', mb: 1, display: 'block' }}>
                {intl.formatMessage({ id: "podSelection" })}
              </Typography>
              <Select
                isMulti
                menuPortalTarget={document.body}
                menuPosition="fixed"
                options={podNames}
                value={podNames.filter((option) => selectedPodId.includes(option.value))}
                onChange={(selectedOptions) => setSelectedPodId(selectedOptions.map((opt) => opt.value))}
                placeholder={intl.formatMessage({ id: "selectPodsRequired" })}
                className="artemy-select-container"
                classNamePrefix="artemy-select"
                styles={{
                  control: (base) => ({
                    ...base,
                    background: 'var(--artemy-bg-elevated)',
                    borderColor: 'var(--artemy-border)',
                    '&:hover': { borderColor: 'var(--artemy-primary)' }
                  }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  menu: (base) => ({
                    ...base,
                    background: 'var(--artemy-bg-elevated)',
                    border: '1px solid var(--artemy-border)',
                    zIndex: 9999
                  }),
                  option: (base, state) => ({
                    ...base,
                    background: state.isFocused ? 'rgba(255,255,255,0.05)' : 'transparent',
                    color: 'var(--artemy-text-primary)'
                  }),
                  multiValue: (base) => ({
                    ...base,
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '4px'
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: 'var(--artemy-text-primary)'
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: 'var(--artemy-text-secondary)'
                  })
                }}
              />
            </Box>

            {selectedPodId?.length > 0 && (
              <>
                <Box sx={{ width: '300px' }}>
                  <Typography variant="caption" sx={{ color: !selectedSprint.name ? '#EF4444' : 'var(--artemy-text-secondary)', mb: 1, display: 'block', fontWeight: !selectedSprint.name ? 700 : 400 }}>
                    Sprint Selection * <span style={{ fontSize: '0.65rem' }}>(COMPULSORY TO LOAD DATA)</span>
                  </Typography>
                  <select
                    className="artemy-input"
                    style={{ 
                      height: '38px', 
                      paddingTop: '4px', 
                      paddingBottom: '4px',
                      border: !selectedSprint.name ? '1px solid #EF4444' : '1px solid var(--artemy-border)',
                      boxShadow: !selectedSprint.name ? '0 0 8px rgba(239, 68, 68, 0.2)' : 'none'
                    }}
                    value={selectedSprint.name}
                    onChange={(e) => {
                      const selectedSprintName = e.target.value;
                      const selectedSprintData = sprintData.find(
                        (sprint) => sprint.sprint_name === selectedSprintName
                      );
                      setSelectedSprint({
                        name: selectedSprintName,
                        startDate: selectedSprintData ? selectedSprintData.sprint_start_date : "",
                        endDate: selectedSprintData ? selectedSprintData.sprint_end_date : "",
                      });
                    }}
                  >
                    <option value="">{intl.formatMessage({ id: "selectSprint" })}</option>
                    {sprintData.map((sprint, index) => (
                      <option key={index} value={sprint.sprint_name}>{sprint.sprint_name}</option>
                    ))}
                  </select>
                </Box>

                <Box sx={{ width: '300px' }}>
                  <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)', mb: 1, display: 'block' }}>
                    {intl.formatMessage({ id: "talentSelection" })}
                  </Typography>
                  <Select
                    isMulti
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    options={Array.isArray(candidates) ? candidates.map(candidate => ({
                      value: candidate.Candidate_Name,
                      label: candidate.Candidate_Name
                    })) : []}
                    value={selectedCandidate}
                    onChange={(selectedOptions) => setSelectedCandidate(selectedOptions || [])}
                    placeholder="Select Candidates"
                    className="artemy-select-container"
                    classNamePrefix="artemy-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        background: 'var(--artemy-bg-elevated)',
                        borderColor: 'var(--artemy-border)',
                        '&:hover': { borderColor: 'var(--artemy-primary)' }
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menu: (base) => ({
                        ...base,
                        background: 'var(--artemy-bg-elevated)',
                        border: '1px solid var(--artemy-border)',
                        zIndex: 9999
                      }),
                      option: (base, state) => ({
                        ...base,
                        background: state.isFocused ? 'rgba(255,255,255,0.05)' : 'transparent',
                        color: 'var(--artemy-text-primary)'
                      }),
                      multiValue: (base) => ({
                        ...base,
                        background: 'rgba(255,255,255,0.1)',
                        borderRadius: '4px'
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: 'var(--artemy-text-primary)'
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: 'var(--artemy-text-secondary)'
                      })
                    }}
                  />
                </Box>
              </>
            )}


          </Box>
        </Box>

        {/* Kanban Board */}
        <Box sx={{ position: 'relative' }}>
          {selectedPodId?.length > 0 && !selectedSprint.name && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3, 
                mx: 'auto', 
                maxWidth: '800px', 
                borderRadius: 2,
                border: '1px solid rgba(239, 68, 68, 0.3)',
                bgcolor: 'rgba(239, 68, 68, 0.05)',
                color: '#EF4444',
                '& .MuiAlert-icon': { color: '#EF4444' }
              }}
            >
              Please select a sprint from the dropdown above to view the tasks for the selected pod.
            </Alert>
          )}
          <button
            className="artemy-btn-circle"
            style={{ position: 'absolute', left: '-20px', top: '50%', zIndex: 10 }}
            onClick={() => handleScroll("left")}
          >
            ←
          </button>

          <DragDropContext onDragEnd={onDragEnd}>
            <Box
              ref={scrollContainerRef}
              sx={{
                display: 'flex',
                gap: 3,
                overflowX: 'auto',
                pb: 4,
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' }
              }}
            >
              {Object.entries(columns).map(([columnId, column]) => (
                <Droppable key={columnId} droppableId={columnId}>
                  {(provided, snapshot) => {
                    const columnSelectedCount = column.items.filter(item => 
                      selectedTasks.includes(item.key || item.pod_task_id)
                    ).length;

                    return (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="artemy-card"
                        sx={{
                          minWidth: '320px',
                          width: '320px',
                          maxHeight: 'calc(100vh - 350px)',
                          display: 'flex',
                          flexDirection: 'column',
                          bgcolor: columnSelectedCount > 0 ? 'rgba(0, 194, 255, 0.05)' : 'rgba(255,255,255,0.02)',
                          borderColor: columnSelectedCount > 0 ? 'rgba(0, 194, 255, 0.2)' : 'rgba(255,255,255,0.05)',
                          boxShadow: columnSelectedCount > 0 ? 'inset 0 0 20px rgba(0, 194, 255, 0.05)' : 'none',
                          transition: 'all 0.3s ease'
                        }}
                      >
                      <Box sx={{
                        p: 2,
                        borderBottom: `2px solid ${column.type === "closed" ? 'var(--artemy-green)' : 'var(--artemy-primary)'}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        bgcolor: columnSelectedCount > 0 ? 'rgba(0, 194, 255, 0.1)' : 'transparent',
                        transition: 'all 0.3s ease'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--artemy-text-primary)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                            {column.title}
                          </Typography>
                          <Chip
                            label={column.items.length}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(255,255,255,0.1)',
                              color: 'var(--artemy-text-secondary)',
                              fontSize: '0.7rem'
                            }}
                          />
                        </Box>
                        
                        {columnSelectedCount > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ color: 'var(--artemy-primary)', fontWeight: 700, fontSize: '0.65rem' }}>
                              {columnSelectedCount} READY
                            </Typography>
                            <IconButton 
                              size="small" 
                              onClick={() => {
                                const columnTaskIds = column.items
                                  .map(item => item.key || item.pod_task_id)
                                  .filter(id => selectedTasks.includes(id));
                                handleStartWorkflow(false, columnTaskIds);
                              }}
                              sx={{ 
                                bgcolor: 'var(--artemy-primary)', 
                                color: '#fff',
                                p: 0.5,
                                '&:hover': { bgcolor: 'var(--artemy-primary-dark, #0099cc)' }
                              }}
                            >
                              <PlayArrowIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        )}
                      </Box>

                      <Box 
                        sx={{ 
                          px: 2, 
                          py: 1.5, 
                          bgcolor: columnSelectedCount > 0 ? 'rgba(0, 194, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)', 
                          borderBottom: '1px solid',
                          borderLeft: `4px solid ${columnSelectedCount > 0 ? 'var(--artemy-primary)' : 'rgba(255, 255, 255, 0.1)'}`,
                          borderColor: columnSelectedCount > 0 ? 'rgba(0, 194, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'all 0.3s ease',
                          boxShadow: columnSelectedCount > 0 ? 'inset 0 0 15px rgba(0, 194, 255, 0.1)' : 'none',
                          mb: 0.5
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SmartToyIcon sx={{ fontSize: 14, color: columnSelectedCount > 0 ? 'var(--artemy-primary)' : 'var(--artemy-text-secondary)' }} />
                            <Typography variant="caption" sx={{ 
                              color: columnSelectedCount > 0 ? 'var(--artemy-primary)' : '#fff', 
                              fontWeight: 800, 
                              fontSize: '0.7rem', 
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              animation: columnSelectedCount > 0 ? 'pulse 2s infinite' : 'none'
                            }}>
                              Agentic Pipeline
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ 
                            color: columnSelectedCount > 0 ? '#fff' : 'rgba(255,255,255,0.5)', 
                            fontSize: '0.65rem', 
                            fontWeight: 500,
                            mt: 0.5
                          }}>
                            {columnSelectedCount > 0 ? `${columnSelectedCount} tasks ready to launch` : 'Select tasks below to build'}
                          </Typography>
                        </Box>
                        
                        {columnSelectedCount > 0 && (
                          <Button 
                            size="small" 
                            variant="contained" 
                            startIcon={<PlayArrowIcon sx={{ fontSize: '12px !important' }} />}
                            onClick={() => {
                              const columnTaskIds = column.items
                                .map(item => item.key || item.pod_task_id)
                                .filter(id => selectedTasks.includes(id));
                              handleStartWorkflow(false, columnTaskIds);
                            }}
                            sx={{ 
                              py: 0, 
                              px: 1.5,
                              minHeight: '24px',
                              fontSize: '0.65rem', 
                              fontWeight: 700,
                              textTransform: 'none',
                              bgcolor: 'var(--artemy-primary)',
                              '&:hover': { bgcolor: '#0099cc' }
                            }}
                          >
                            Build
                          </Button>
                        )}
                      </Box>

                      <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pb: 2 }}>
                        {loading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress size={24} sx={{ color: 'var(--artemy-primary)' }} />
                          </Box>
                        ) : column.items.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 8, opacity: 0.3 }}>
                            <Typography variant="caption">No items</Typography>
                          </Box>
                        ) : (
                          column.items.map((task, index) => {
                            const taskId = task.key || task.pod_task_id;
                            const isSelected = selectedTasks.includes(taskId);

                            return (
                              <Draggable
                                key={taskId}
                                draggableId={taskId.toString()}
                                index={index}
                              >
                                {(provided) => (
                                  <Box
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className="artemy-card artemy-card-hover"
                                    onClick={() => handleTaskClick(task)}
                                    sx={{
                                      mb: 2,
                                      p: 2,
                                      borderLeft: `4px solid ${column.type === "closed" ? 'var(--artemy-green)' : 'var(--artemy-primary)'}`,
                                      bgcolor: isSelected ? 'rgba(0, 194, 255, 0.15)' : 'var(--artemy-bg-elevated)',
                                      boxShadow: isSelected ? '0 0 15px rgba(0, 194, 255, 0.2)' : 'none',
                                      borderColor: isSelected ? 'var(--artemy-primary)' : 'var(--artemy-border)',
                                      position: 'relative',
                                      transition: 'all 0.2s ease',
                                      '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                                      }
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        background: isSelected ? 'rgba(0, 194, 255, 0.1)' : 'rgba(0,0,0,0.2)',
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        border: '1px solid',
                                        borderColor: isSelected ? 'var(--artemy-primary)' : 'transparent'
                                      }}
                                      title="Select to launch Agentic AI Pipeline"
                                    >
                                      {isSelected && <Typography variant="caption" sx={{ color: 'var(--artemy-primary)', fontWeight: 700, fontSize: '0.6rem', textTransform: 'uppercase' }}>Selected</Typography>}
                                      <Checkbox
                                        size="small"
                                        checked={isSelected}
                                        onChange={(e) => handleTaskSelection(taskId, e)}
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{
                                          p: 0,
                                          color: 'var(--artemy-text-secondary)',
                                          '&.Mui-checked': { color: 'var(--artemy-primary)' }
                                        }}
                                      />
                                    </Box>

                                    <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)', mb: 1, display: 'block' }}>
                                      {taskId}
                                    </Typography>

                                    <Box sx={{ display: 'flex', gap: 1, mb: 1, pr: 3 }}>
                                      {task.key ? (
                                        <FaJira size={14} color="var(--artemy-primary)" style={{ flexShrink: 0, marginTop: 4 }} />
                                      ) : (
                                        <FaBug size={14} color="var(--artemy-green)" style={{ flexShrink: 0, marginTop: 4 }} />
                                      )}
                                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--artemy-text-primary)' }}>
                                        {task.summary || task.pod_task_name}
                                      </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PersonIcon sx={{ fontSize: 14, color: 'var(--artemy-text-secondary)' }} />
                                        <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)' }}>
                                          {task.Candidate_Name || 'Unassigned'}
                                        </Typography>
                                      </Box>
                                      {task.assigned_agent && (
                                        <Tooltip title={`Agent: ${task.assigned_agent.name}`}>
                                          <SmartToyIcon sx={{ fontSize: 16, color: 'var(--artemy-primary)' }} />
                                        </Tooltip>
                                      )}
                                    </Box>

                                    <Typography variant="caption" sx={{ mt: 1, color: 'var(--artemy-text-secondary)', display: 'block', fontSize: '0.7rem' }}>
                                      {intl.formatMessage({ id: "dueDate" })}: {moment(task.due_date).format("MM-DD-YYYY") || "Not Set"}
                                    </Typography>
                                  </Box>
                                )}
                              </Draggable>
                            );
                          })
                        )}
                        {provided.placeholder}
                        </Box>
                      </Box>
                    );
                  }}
                  </Droppable>
              ))}
            </Box>
          </DragDropContext>

          <button
            className="artemy-btn-circle"
            style={{ position: 'absolute', right: '-20px', top: '50%', zIndex: 10 }}
            onClick={() => handleScroll("right")}
          >
            →
          </button>
        </Box>

        {/* Action Bar - Unified Flow */}
        <Slide direction="up" in={selectedTasks.length > 0} mountOnEnter unmountOnExit>
          <Box sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'auto',
            minWidth: '600px',
            bgcolor: 'var(--artemy-bg-elevated)',
            border: '1px solid var(--artemy-primary)',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            zIndex: 1100,
            backdropFilter: 'blur(20px)'
          }}>
            <Box sx={{ ml: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--artemy-text-primary)' }}>
                {selectedTasks.length} {selectedTasks.length === 1 ? 'task' : 'tasks'} selected
              </Typography>
              <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)' }}>
                Ready to assign agents and generate code
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mr: 1 }}>
              <button
                className="artemy-btn"
                style={{ background: 'transparent', border: '1px solid var(--artemy-border)' }}
                onClick={handleClearSelection}
              >
                Clear
              </button>
              <button
                className="artemy-btn"
                onClick={handleCompleteFlow}
                disabled={isLaunchingPipeline}
                style={{
                  background: isLaunchingPipeline ? 'rgba(0,194,255,0.3)' : 'linear-gradient(135deg, #00C2FF, #7B2FFF)',
                  border: 'none',
                  fontWeight: 700,
                  letterSpacing: '0.03em',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                {isLaunchingPipeline ? '🔄 Building...' : '🚀 Build'}
              </button>
            </Box>
          </Box>
        </Slide>

        {/* Pipeline Result Dialog */}
        <Dialog
          open={showPipelineResult}
          onClose={closePipelineResult}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: theme.palette.mode === 'dark' ? '#1E1E1E' : '#FFFFFF',
              color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
              backgroundImage: 'none',
              border: `1px solid ${pipelineResult?.success ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.4)'}`,
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
            }
          }}
        >
          <DialogTitle sx={{ borderBottom: `1px solid ${theme.palette.divider}`, pb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h5">{pipelineResult?.success ? '✅' : '❌'}</Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: pipelineResult?.success ? '#10B981' : '#EF4444' }}>
                {pipelineResult?.success ? 'Pipeline Launched!' : 'Launch Failed'}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {pipelineResult?.success ? (
              <Box>
                <Typography variant="body1" sx={{ color: theme.palette.mode === 'dark' ? '#E5E7EB' : '#374151', mb: 2 }}>
                  Successfully submitted <strong>{pipelineResult.count}</strong> task{pipelineResult.count !== 1 ? 's' : ''} to the Agentic AI pipeline.
                </Typography>

                {/* Workflow / Task IDs */}
                <Box sx={{ background: theme.palette.mode === 'dark' ? '#0A1628' : '#F3F4F6', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', p: 2, mb: 2 }}>
                  {pipelineResult.data?.workflow_id && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="caption" sx={{ color: theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280' }}>Workflow ID</Typography>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#00C2FF' }}>
                        {String(pipelineResult.data.workflow_id).substring(0, 8)}…
                      </Typography>
                    </Box>
                  )}
                  {pipelineResult.data?.task_id && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="caption" sx={{ color: theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280' }}>Task ID</Typography>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', color: theme.palette.mode === 'dark' ? '#94A3B8' : '#374151' }}>
                        {String(pipelineResult.data.task_id).substring(0, 8)}…
                      </Typography>
                    </Box>
                  )}

                  {/* Deployed URL link — polls until available and health passes */}
                  {deployedUrl ? (
                    <Box
                      component="a"
                      href={deployedUrl}
                      target="_blank"
                      rel="noreferrer"
                      sx={{
                        display: 'block',
                        mt: 1,
                        p: 1.5,
                        background: 'rgba(0,194,255,0.1)',
                        borderRadius: '8px',
                        color: '#00C2FF',
                        textAlign: 'center',
                        textDecoration: 'none',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        border: '1px solid rgba(0,194,255,0.2)',
                        '&:hover': { background: 'rgba(0,194,255,0.18)' }
                      }}
                    >
                      View Deployed Application 🚀
                    </Box>
                  ) : checkingDeploy ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, p: 1.5, mt: 1, background: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px dashed rgba(255,255,255,0.1)' }}>
                       <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#00C2FF', animation: 'pulse 1.5s infinite' }} />
                       <Typography variant="caption" sx={{ color: theme.palette.mode === 'dark' ? '#94A3B8' : '#64748B' }}>
                         {deployStatus ? `Status: ${deployStatus.charAt(0).toUpperCase() + deployStatus.slice(1)}...` : 'Checking deployment status...'}
                       </Typography>
                    </Box>
                  ) : (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: theme.palette.mode === 'dark' ? '#64748B' : '#9CA3AF' }}>
                      Deployment URL will be available once the pipeline completes.
                    </Typography>
                  )}
                </Box>

                <Typography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? '#9CA3AF' : '#6B7280' }}>
                  Track live progress in the{' '}
                  <strong
                    style={{ color: '#00C2FF', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => {
                      closePipelineResult();
                      window.location.href = '/Workflow';
                    }}
                  >
                    Build Workflow
                  </strong>{' '}
                  workspace.
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography variant="body1" sx={{ color: theme.palette.mode === 'dark' ? '#E5E7EB' : '#374151', mb: 2 }}>
                  Could not launch pipeline for {pipelineResult?.count} task{pipelineResult?.count !== 1 ? 's' : ''}.
                </Typography>
                <Typography variant="body2" sx={{ color: '#EF4444' }}>
                  {pipelineResult?.error || 'An unexpected error occurred. Please try again.'}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Button variant="contained" onClick={closePipelineResult} sx={{ bgcolor: '#00C2FF', color: '#fff', '&:hover': { bgcolor: '#009BCC' } }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Agent Selection Dialog */}
        <Dialog
          open={showAgentSelectionDialog}
          onClose={handleCloseAgentDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            className: "artemy-card",
            sx: { bgcolor: 'var(--artemy-bg-elevated)', backgroundImage: 'none', border: '1px solid var(--artemy-border)' }
          }}
        >
          <DialogTitle sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)', pb: 2, bgcolor: '#111' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SmartToyIcon sx={{ color: 'var(--artemy-primary, #00c2ff)' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff' }}>Select AI Agent</Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 2, minHeight: '300px', bgcolor: '#0d0e12' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 3 }}>
              Assign an AI agent to {selectedTasks.length} {selectedTasks.length === 1 ? 'task' : 'tasks'}.
              The agent will handle code generation and submission.
            </Typography>

            {agents.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {agents.map((agent) => {
                  const isSelected = selectedAgentForBulk === agent.id;
                  return (
                    <Box
                      key={agent.id}
                      onClick={() => setSelectedAgentForBulk(agent.id)}
                      sx={{
                        p: 2,
                        borderRadius: '12px',
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: isSelected ? 'var(--artemy-primary, #00c2ff)' : 'rgba(255,255,255,0.1)',
                        bgcolor: isSelected ? 'rgba(0, 194, 255, 0.1)' : 'rgba(255,255,255,0.03)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.07)',
                          borderColor: isSelected ? 'var(--artemy-primary, #00c2ff)' : 'rgba(255,255,255,0.3)'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: isSelected ? '#00c2ff' : '#fff' }}>
                            {agent.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                            {agent.agent_type}
                          </Typography>
                        </Box>
                        <Chip
                          label={agent.status || 'Active'}
                          size="small"
                          sx={{
                            bgcolor: agent.status === 'busy' ? 'rgba(255,165,0,0.2)' : 'rgba(0,255,0,0.1)',
                            color: agent.status === 'busy' ? 'orange' : '#00ff00',
                            fontSize: '0.65rem'
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px dashed rgba(255,255,255,0.1)' }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.4)' }}>
                  No agents found for this organization.
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)', display: 'block', mt: 1 }}>
                  Please ensure agents are configured in your workspace.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(255,255,255,0.1)', bgcolor: '#111' }}>
            <Button sx={{ color: 'rgba(255,255,255,0.6)' }} onClick={handleCloseAgentDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => handleStartWorkflow()}
              disabled={!selectedAgentForBulk || isLaunchingPipeline}
              sx={{
                bgcolor: 'var(--artemy-primary, #00c2ff)',
                '&:disabled': { bgcolor: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.2)' }
              }}
            >
              {isLaunchingPipeline ? 'Building...' : 'Build Pipeline'}
            </Button>
          </DialogActions>
        </Dialog>


        {/* Progress Dialog */}
        <Dialog
          open={isGeneratingCode}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            className: "artemy-card",
            sx: { bgcolor: 'var(--artemy-bg-elevated)', backgroundImage: 'none' }
          }}
        >
          <DialogContent>
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <CircularProgress size={60} sx={{ mb: 4, color: 'var(--artemy-primary)' }} />
              <Typography variant="h6" className="artemy-main" sx={{ mb: 2 }}>{progressMessage}</Typography>
              <Box sx={{ width: '100%', bgcolor: 'rgba(255,255,255,0.05)', borderRadius: '10px', height: '8px', overflow: 'hidden', mb: 2 }}>
                <Box sx={{ width: `${currentProgress}%`, height: '100%', bgcolor: 'var(--artemy-primary)', transition: 'width 0.3s ease' }} />
              </Box>
              <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)' }}>
                {Math.round(currentProgress)}% Complete
              </Typography>
            </Box>
          </DialogContent>
        </Dialog>

        {/* Task Details Drawer */}
        <Drawer
          anchor="right"
          open={!!selectedTask}
          onClose={handleDrawerClose}
          PaperProps={{
            sx: { width: 600, bgcolor: 'var(--artemy-bg-elevated)', backgroundImage: 'none', borderLeft: '1px solid var(--artemy-border)', color: 'var(--artemy-text-primary)' }
          }}
        >
          <Box sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle2" sx={{ color: 'var(--artemy-primary)', letterSpacing: '2px', textTransform: 'uppercase' }}>
                  Task Assignment
                </Typography>
              </Box>
              <IconButton onClick={handleDrawerClose} sx={{ color: 'var(--artemy-text-secondary)' }}><CloseIcon /></IconButton>
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              {selectedTask?.key || selectedTask?.pod_task_id}
            </Typography>
            <Typography variant="body1" sx={{ color: 'var(--artemy-text-secondary)', mb: 4 }}>
              {selectedTask?.summary || selectedTask?.pod_task_name}
            </Typography>

            <Box className="artemy-card" sx={{ p: 3, mb: 4 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)', display: 'block', mb: 0.5 }}>Assignee</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon size={14} sx={{ color: 'var(--artemy-green)' }} />
                    <Typography variant="body2">{selectedTask?.Candidate_Name || 'Not Assigned'}</Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)', display: 'block', mb: 0.5 }}>Due Date</Typography>
                  <Typography variant="body2">{moment(selectedTask?.due_date).format("MMM DD, YYYY") || "Not Set"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)', display: 'block', mb: 0.5 }}>Status</Typography>
                  <Chip label={selectedTask?.status} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.05)', color: 'var(--artemy-text-primary)' }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)', display: 'block', mb: 0.5 }}>Priority</Typography>
                  <Typography variant="body2">{selectedTask?.priority || 'Normal'}</Typography>
                </Box>
              </Box>
            </Box>



            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Discussion</Typography>
            <Box sx={{ flex: 1, overflowY: 'auto', mb: 3 }}>
              {renderComments()}
            </Box>

            <Box sx={{ mt: 'auto', pt: 3, borderTop: '1px solid var(--artemy-border)' }}>
              <textarea
                className="artemy-input"
                style={{ width: '100%', height: '80px', marginBottom: '16px', background: 'rgba(255,255,255,0.03)' }}
                placeholder="Add a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
              />
              <button
                className="artemy-btn"
                style={{ float: 'right' }}
                onClick={handleAddComment}
                disabled={!newComment.trim()}
              >
                Post Comment
              </button>
            </Box>
          </Box>
        </Drawer>

        {/* Global Notification */}
        <Snackbar
          open={showNotification}
          autoHideDuration={6000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <div className="artemy-card" style={{
            padding: '12px 24px',
            background: notificationMessage.includes('✅') ? 'var(--artemy-green)' : 'var(--artemy-primary)',
            color: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <Typography variant="body2">{notificationMessage}</Typography>
          </div>
        </Snackbar>
      </Box>
    </div>
  );
};

export default KanbanBoard;